import { useEffect, useRef, useState } from 'react';
import client from '../api/client';
import { Info, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@pinpoint/ui/button';
import { Input } from '../components/ui/input';
import GateLayout from '../layouts/GateLayout';
import { BETA_LANDING_PATH, consumeSafeReturn } from '../lib/safeReturn';
import { expireProductSession } from '../lib/sessionExpiry';
import { normalizeCode } from './normalizeAccessCode';
import { useCanonicalProductPath } from '../next/app/navigation/ProductNavigation';

/**
 * AccessPending — the closed-beta redeem page (Wave 3).
 *
 * Hosted in GateLayout (D3). Implements the 8-state matrix from the Design UX
 * doc + cross-cutting decisions:
 *   idle | format_invalid (inline red, R22) | submitting | success |
 *   server_rejected (neutral banner, R22) | rate_limited | session_expired | network_failure
 *
 * On mount: emits access_pending_viewed ONCE per session (R19 anchor for
 * time_to_redeem_ms), guarded against React 18 double-mount.
 *
 * On success: consumes the canonical timestamped Product safe return and
 * redirects once (default = BETA_LANDING_PATH, currently /practice).
 */

const CODE_LEN = 10;
const DEFAULT_NEXT = BETA_LANDING_PATH;
const VIEWED_GUARD_KEY = 'access_pending_viewed_fired';

function resolveNext() {
    try {
        return consumeSafeReturn();
    } catch {
        /* sessionStorage unavailable */
    }
    return DEFAULT_NEXT;
}

export default function AccessPending() {
    const canonicalProductPath = useCanonicalProductPath();
    const [code, setCode] = useState('');
    const [state, setState] = useState({ kind: 'idle' });
    const viewedFiredRef = useRef(false);
    const headingRef = useRef(null);
    const countdownRef = useRef(null);
    const redirectTimerRef = useRef(null);

    // R19: emit access_pending_viewed once per session (double-mount guarded).
    useEffect(() => {
        headingRef.current?.focus();
    }, []);

    useEffect(() => {
        if (viewedFiredRef.current) return;
        let already = false;
        try {
            already = sessionStorage.getItem(VIEWED_GUARD_KEY) === '1';
        } catch {
            /* ignore */
        }
        if (already) {
            viewedFiredRef.current = true;
            return;
        }
        viewedFiredRef.current = true;
        try {
            sessionStorage.setItem(VIEWED_GUARD_KEY, '1');
        } catch {
            /* ignore */
        }
        client.post('/api/access/pending-viewed').catch(() => {
            /* best-effort analytics; never block the page */
        });
    }, []);

    useEffect(() => () => {
        clearInterval(countdownRef.current);
        if (redirectTimerRef.current !== null) {
            clearTimeout(redirectTimerRef.current);
        }
    }, []);

    const normalized = normalizeCode(code);
    const isComplete = normalized.length === CODE_LEN;
    const isSubmitting = state.kind === 'submitting';
    const isRateLimited = state.kind === 'rate_limited';
    const canSubmit = isComplete && !isSubmitting && !isRateLimited;

    function startRateLimitCountdown(seconds) {
        clearInterval(countdownRef.current);
        let remaining = seconds;
        setState({ kind: 'rate_limited', remaining });
        countdownRef.current = setInterval(() => {
            remaining -= 1;
            if (remaining <= 0) {
                clearInterval(countdownRef.current);
                setState({ kind: 'idle' });
            } else {
                setState({ kind: 'rate_limited', remaining });
            }
        }, 1000);
    }

    async function handleSubmit(e) {
        if (e) e.preventDefault();
        if (!isComplete) {
            setState({ kind: 'format_invalid', entered: normalized.length });
            return;
        }
        if (!canSubmit) return;

        setState({ kind: 'submitting' });
        const requestToken = localStorage.getItem('access_token');
        try {
            const res = await client.post('/api/access/redeem', { code: normalized });
            if (res?.data?.ok) {
                setState({ kind: 'success' });
                const next = canonicalProductPath(resolveNext());
                try {
                    // consumeSafeReturn removes the one-shot value before use.
                } catch {
                    /* ignore */
                }
                if (redirectTimerRef.current !== null) {
                    clearTimeout(redirectTimerRef.current);
                }
                redirectTimerRef.current = setTimeout(() => {
                    redirectTimerRef.current = null;
                    window.location.assign(next);
                }, 800);
            } else {
                setState({ kind: 'server_rejected' });
                setCode('');
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) {
                const ra = Number(err?.response?.data?.retry_after_s) || 60;
                startRateLimitCountdown(ra);
            } else if (status === 401) {
                const expired = expireProductSession('/access-pending', requestToken);
                setState({ kind: expired ? 'session_expired' : 'idle' });
            } else if (status && status >= 400 && status < 500) {
                setState({ kind: 'server_rejected' });
                setCode('');
            } else {
                setState({ kind: 'network_failure' });
            }
        }
    }

    function handleChange(e) {
        setCode(e.target.value);
        // Clear transient error/banner states as the user types a fresh attempt.
        if (state.kind === 'format_invalid' || state.kind === 'network_failure') {
            setState({ kind: 'idle' });
        }
    }

    if (state.kind === 'success') {
        return (
            <GateLayout>
                <div className="flex flex-col items-center text-center gap-3 py-8" role="status">
                    <CheckCircle2 className="h-12 w-12 text-green-600" aria-hidden="true" />
                    <h2 className="text-2xl font-semibold">You're in.</h2>
                    <p className="text-sm text-muted-foreground">Taking you to Pinpoint...</p>
                </div>
            </GateLayout>
        );
    }

    return (
        <GateLayout>
            <div className="rounded-lg border bg-card p-8 shadow-sm">
                <h1 ref={headingRef} tabIndex={-1} className="text-2xl font-semibold">You're signed in.</h1>
                <hr className="my-6" />

                <p className="text-base leading-relaxed">
                    PinpointEdu is in closed beta. To get in, enter the invite code we sent you.
                </p>

                {/* Server-rejected: neutral banner (R22) — Info icon, bg-muted, NOT red. */}
                {state.kind === 'server_rejected' && (
                    <div
                        className="mt-4 flex items-start gap-2 rounded-md bg-muted p-3 text-sm"
                        role="alert"
                        data-testid="server-rejected-banner"
                    >
                        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                        <span>This code can't be used. Check it, or contact whoever invited you.</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="mt-6">
                    <label htmlFor="invite-code" className="block text-sm font-medium">
                        Invite code
                    </label>
                    <Input
                        id="invite-code"
                        name="invite-code"
                        value={code}
                        onChange={handleChange}
                        placeholder="ABCD-1234-XY"
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                        disabled={isSubmitting}
                        className="mt-2 h-12 font-mono text-lg"
                        aria-describedby="invite-help"
                    />

                    {/* Format-invalid: inline red text (R22), no banner. */}
                    {state.kind === 'format_invalid' ? (
                        <p id="invite-help" className="mt-2 text-xs text-destructive" role="alert">
                            Codes are {CODE_LEN} characters. You've entered {state.entered}.
                        </p>
                    ) : state.kind === 'rate_limited' ? (
                        <p id="invite-help" className="mt-2 text-xs text-destructive" role="alert" aria-live="polite">
                            Too many tries. Wait {state.remaining} seconds and try again.
                        </p>
                    ) : state.kind === 'network_failure' ? (
                        <p id="invite-help" className="mt-2 text-xs text-destructive" role="alert">
                            Couldn't reach the server. Check your connection and try again.
                        </p>
                    ) : (
                        <p id="invite-help" className="mt-2 text-xs text-muted-foreground">
                            {isSubmitting
                                ? 'Checking your code…'
                                : `${CODE_LEN} characters, letters and numbers. Not case-sensitive.`}
                        </p>
                    )}

                    <Button
                        type="submit"
                        disabled={!canSubmit}
                        className="mt-6 h-12 w-full"
                        data-testid="redeem-submit"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                                Checking your code…
                            </>
                        ) : isRateLimited ? (
                            `Try again in ${state.remaining}s`
                        ) : (
                            'Unlock access'
                        )}
                    </Button>
                </form>

                <hr className="my-6" />
                <p className="text-sm">
                    Don't have a code?{' '}
                    <a href="mailto:yangmu@pinpointedu.com" className="text-blue-700 hover:text-blue-800 hover:underline">
                        Request beta access
                    </a>
                </p>
            </div>
        </GateLayout>
    );
}
